import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Progress,
  Row,
  Select,
  Skeleton,
  Typography,
  Upload,
  message,
} from "antd";
import RegisterRoleSvg from "../../../assets/register.svg";
import { UploadOutlined, ClockCircleOutlined } from "@ant-design/icons";
import "react-quill/dist/quill.snow.css";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import ErrorPage from "components/ui/ErrorPage";
import { useAddressHelper } from "utils/useAddressHelper";
import { useUploadFiles } from "utils/media";
import { FileTypes } from "enum/file-type";
import { isJSONString } from "utils/validate-json";
import { useUpdateReacruiterProfile } from "api/recruiter/Profile/useUpdateProfile";
import { useGetRecruiterProfileInfo } from "api/recruiter/Profile/useGetProfile";
import { phoneValidator } from "utils/phone-validator";
import { numberValidator } from "utils/number-validator";
import { RoleTypes } from "enum/role-type-enum";

const { Title } = Typography;
const { Option } = Select;

const RecruiterProfile = () => {
  const [form] = Form.useForm();

  const { id } = useParams();
  const navigate = useNavigate();

  const updateProfileMutation = useUpdateReacruiterProfile();

  const { data, error, isPending } = useGetRecruiterProfileInfo(id);

  const {
    address,
    setAddress,
    getProvinceOptions,
    getDistrictOptions,
    getMunicipalityOptions,
    getWardOptions,
  } = useAddressHelper();

  const { uploadImage, onChange, beforeUpload, normFile, getFileLink, cvID, setCVId, uploading } =
    useUploadFiles();

  React.useEffect(() => {
    if (!data) return;
    const initialiseFormFiles = async () => {
      if (data?.companyLogo?._id) {
        setCVId(data?.companyLogo?._id);
        const file = await getFileLink(data?.companyLogo?._id as string);

        form.setFieldsValue({ companyLogo: file });
      } else {
        form.setFieldsValue({ companyLogo: [] });
      }
    };

    initialiseFormFiles();
  }, [data,form,getFileLink,setCVId]);

  React.useEffect(() => {
    if (!data) return;
    form.setFieldsValue({
      companyName: data?.companyName ? data.companyName : "",
      noOfEmployees: data?.noOfEmployees ? data.noOfEmployees : "",
      registrationNumber: data?.registrationNumber ? data.registrationNumber : "",
      address: data?.address ? data.address : null,
      email: data?.email ? data.email : "",
      userName: data?.userName ? data.userName : "",
      phoneNumber: data?.phoneNumber ? data.phoneNumber : "",
    });
  }, [data,form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const payloads = {
      ...values,
      companyLogo: cvID,

      address: {
        ...values.address,
        state: isJSONString(values?.address?.state)
          ? JSON.parse(values.address.state)?.name
          : values.address.state,
      },

      role: RoleTypes.RECRUITER,
    };

    if (id) {
      updateProfileMutation.mutate(
        { id: id, recruiter: payloads },
        {
          onSuccess: () => {
            form.resetFields();
            navigate("/recruiter/home");
          },
        }
      );
    } else {
      message.error("User id not found");
    }
  };

  if (isPending) {
    return (
      <div className="bg-background min-h-screen">
        <div className="max-w-6xl mx-auto py-12">
          <Card style={{ width: "100%", marginTop: 16, height: "90vh" }}>
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <div className="bg-background">
        <div className="max-w-6xl mx-auto py-12">
          <Card className="px-8 py-2">
            <div className="flex justify-between items-center">
              <Title level={2} style={{ fontWeight: 700 }}>
                Lets's setup your profile
              </Title>
              <img alt="#" src={RegisterRoleSvg} />
            </div>
          </Card>

          <Form layout="vertical" form={form} onFinish={handleSubmit}>
            <Card className="px-8 py-2 mt-8">
              <Row gutter={[48, 32]}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                    ]}
                  >
                    <Input placeholder="eg. Royal Remit Pvt Ltd" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name="userName"
                    label="User Name"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                    ]}
                  >
                    <Input placeholder="eg. microsoft_username" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                  <Form.Item
                    name="email"
                    label="Email address"
                    rules={[
                      {
                        required: true,
                        message: "Email is required",
                      },
                      {
                        type: "email",
                        message: "Please enter valid email",
                      },
                    ]}
                  >
                    <Input placeholder="eg. example@gmail.com" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name="noOfEmployees"
                    label="Number of employees"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                      {
                        validator: numberValidator,
                      },
                    ]}
                  >
                    <Input placeholder="eg. 2" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name="registrationNumber"
                    label="Registration Number"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                      {
                        validator: numberValidator,
                      },
                    ]}
                  >
                    <Input placeholder="eg. 1453773878484" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name="phoneNumber"
                    label="Phone Number"
                    rules={[
                      {
                        required: true,
                        message: "Phone number is required",
                      },
                      {
                        validator: phoneValidator,
                      },
                    ]}
                  >
                    <Input placeholder="eg. 9848442507" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    label="Province"
                    name={["address", "state"]}
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      placeholder="Select province"
                      options={getProvinceOptions()}
                      onChange={(value: string) => setAddress({ ...address, province: value })}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name={["address", "district"]}
                    label="District"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Select district"
                      style={{ width: "100%" }}
                      optionFilterProp="children"
                      filterOption={(input, option: any) => {
                        return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                      onChange={(value: string) => setAddress({ ...address, district: value })}
                    >
                      {getDistrictOptions()?.map((district) => (
                        <Option key={district?.label} value={district?.label}>
                          {district?.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name={["address", "municipality"]}
                    label="Municipality"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Select municipality"
                      style={{ width: "100%" }}
                      optionFilterProp="children"
                      filterOption={(input, option: any) => {
                        return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                      }}
                      onChange={(value: string) => setAddress({ ...address, municipality: value })}
                    >
                      {getMunicipalityOptions()?.map((municipality) => (
                        <Option key={municipality?.label} value={municipality?.label}>
                          {municipality?.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item
                    name={["address", "ward"]}
                    label="Ward"
                    rules={[
                      {
                        required: true,
                        message: "This is required",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      placeholder="Select ward"
                      options={getWardOptions()}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Title level={2}>Upload Company Logo</Title>
                  <Form.Item
                    name={["companyLogo"]}
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[
                      {
                        required: true,
                        message: "Please upload your logo.",
                      },
                    ]}
                  >
                    <Upload
                      maxCount={1}
                      multiple={false}
                      onChange={onChange}
                      customRequest={(options) =>
                        uploadImage(options, {
                          type: FileTypes.CV,
                          service: "recruiter",
                          tags: ["recruiter", "logo"],
                          description: "logo of recruiter",
                        })
                      }
                      beforeUpload={beforeUpload}
                      listType="picture"
                      progress={{ showInfo: true }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        className="h-12 px-3 bg-background text-base font-bold flex items-center gap-2"
                      >
                        Click to upload
                      </Button>
                    </Upload>
                  </Form.Item>
                  {uploading && <Progress percent={50} />}
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Form.Item name="isTermAgreed" valuePropName="checked">
                    <Checkbox>
                      By clicking this box and providing your number, you agree to receive marketing
                      and informational calls and texts from Hire enumerator.
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <div className="flex justify-between  pt-12">
                <Button
                  className="h-12 px-8 border-2 border-border text-base  flex items-center gap-2"
                  onClick={() => navigate("/enumerator/home")}
                >
                  <ClockCircleOutlined />
                  Cancel
                </Button>
                <Button
                  className="h-12 px-8 border-2 border-border text-base  flex items-center gap-2 bg-primary text-white border-none"
                  htmlType="submit"
                  loading={updateProfileMutation.isPending}
                  type="primary"
                >
                  Save
                </Button>
              </div>
            </Card>
          </Form>
        </div>
      </div>
    </>
  );
};

export default RecruiterProfile;
